import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppData } from '../context/AppDataContext';
import { ragSourceTypeLabel, resolveRagSourcePath } from '../lib/ragSourceLinks';
import type { ChatCitation } from '../types';
import GlobalCollapsibleButton from './GlobalCollapsibleButton';
import styles from './ChatCitations.module.css';

export type ChatCitationsProps = {
  citations: ChatCitation[];
};

export function ChatCitations({ citations }: ChatCitationsProps) {
  const { updates, monthlyUpdates } = useAppData();

  const linkContext = useMemo(
    () => ({
      companyUpdateCompanyId: (updateId: string) =>
        updates.find((entry) => entry.id === updateId)?.companyId,
      monthlyUpdateCompanyId: (updateId: string) =>
        monthlyUpdates.find((entry) => entry.id === updateId)?.companyId,
    }),
    [updates, monthlyUpdates]
  );

  if (!citations.length) {
    return null;
  }

  return (
    <GlobalCollapsibleButton
      title={`Sources (${citations.length})`}
      size="comfortable"
      className={styles.sourcesCollapsible}
    >
      <ul className={styles.list}>
        {citations.map((citation, index) => {
          const label = ragSourceTypeLabel(citation.sourceType);
          const path = resolveRagSourcePath(citation.sourceType, citation.sourceId, linkContext);
          const key = `${citation.sourceType}-${citation.sourceId}-${index}`;

          if (path) {
            return (
              <li key={key} className={styles.item}>
                <Link to={path} className={styles.citationLink}>
                  <span className={styles.sourceType}>{label}</span>
                  <span className={styles.excerpt}>{citation.excerpt}</span>
                </Link>
              </li>
            );
          }

          return (
            <li key={key} className={styles.item}>
              <div className={styles.citationStatic}>
                <span className={styles.sourceType}>{label}</span>
                <span className={styles.excerpt}>{citation.excerpt}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </GlobalCollapsibleButton>
  );
}
